import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Home,
  CreditCard,
  Repeat,
  DollarSign,
  BarChart2,
  Database,
  Lock,
  Mail,
  LogOut,
  Handshake,
  BadgeDollarSign,
} from 'lucide-react';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const MENU_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: Home },
  { label: 'Transações', path: '/dashboard/transactions', icon: Handshake },
  { label: 'Parcelas', path: '/dashboard/installments', icon: CreditCard },
  { label: 'Contas Recorrentes', path: '/dashboard/recurring', icon: Repeat },
  { label: 'Fontes de Renda', path: '/dashboard/incomes', icon: DollarSign },
  { label: 'Projeções', path: '/dashboard/projections', icon: BarChart2 },
];

const CONFIG_ITEMS = [
  { label: 'Alterar Senha', path: '/dashboard/change-password', icon: Lock },
  { label: 'Backup', path: '/dashboard/backup', icon: Database },
  { label: 'Contato', path: '/dashboard/contact', icon: Mail },
];

export default function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const location = useLocation();
  const { user, handleLogout } = useAuth();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const iconClass = 'w-5 h-5 text-gray-600';

  return (
    <>
      {/* Desktop Sidebar - always visible, pastel minimalist style */}
      <div className="hidden md:flex md:w-56 md:h-screen md:flex-col md:bg-white md:text-gray-900 md:shadow md:fixed md:left-0 md:top-0 md:border-r md:border-gray-100">
        {/* Logo/Header */}
        <div className="px-6 pt-8 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center text-white">
              <BadgeDollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-md font-semibold">FinanceApp</h1>
              <p className="text-xs text-gray-500">Controle financeiro</p>
            </div>
          </div>
        </div>

        {/* Main Menu */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-400 px-2 mb-3 uppercase tracking-wider">Menu</p>
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150 ${
                  isActive(item.path)
                    ? 'bg-gray-50 font-semibold text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className={iconClass} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Config Menu */}
        <div className="px-3 mb-6">
          <p className="text-xs font-semibold text-gray-400 px-2 mb-3 uppercase tracking-wider">Configurações</p>
          <div className="space-y-1">
            {CONFIG_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150 ${
                    isActive(item.path)
                      ? 'bg-gray-50 font-semibold text-gray-900'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={iconClass} />
                  <span className="text-sm">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* User Footer */}
        <div className="px-4 py-4 border-t border-gray-100 bg-white space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-700 font-medium">{user?.name?.charAt(0) || 'U'}</div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={() => {
              handleLogout();
              onClose?.();
            }}
            className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-md py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="w-4 h-4 text-gray-600" /> Sair
          </button>
        </div>
      </div>

      {/* Mobile Sidebar - overlay (appears over content) with same pastel style */}
      <div
        className={`fixed left-0 top-0 h-screen w-64 bg-white text-gray-900 shadow-lg transition-transform duration-200 z-50 md:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-md text-gray-600">
          ✕
        </button>

        <div className="px-6 pt-8 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-700">
              <BadgeDollarSign className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-md font-semibold">FinanceApp</h1>
              <p className="text-xs text-gray-500">Controle financeiro</p>
            </div>
          </div>
        </div>

        <nav className="px-3 py-6 space-y-1 overflow-y-auto">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150 ${
                  isActive(item.path) ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className={iconClass} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 mt-6">
          {CONFIG_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.label}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150 ${
                  isActive(item.path) ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className={iconClass} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-4 py-4 border-t border-gray-100 bg-white space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-700 font-medium">{user?.name?.charAt(0) || 'U'}</div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={() => { handleLogout(); onClose?.(); }} className="w-full flex items-center justify-center gap-2 border border-gray-200 rounded-md py-2 text-sm text-gray-700 hover:bg-gray-50">
            <LogOut className="w-4 h-4 text-gray-600" /> Sair
          </button>
        </div>
      </div>
    </>
  );
}
